import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconChevronRight, IconPencil, IconPlayerPlay, IconPlus } from '@tabler/icons-react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import useLlmModels, { withCurrent } from '../../helpers/useLlmModels';
import ErrorMessage from '../ErrorMessage';
import HelpPopover from '../HelpPopover/HelpPopover';
import SettingRow from '../SettingRow/SettingRow';
import Spinner from '../Spinner';
import { HELP } from './aiModelsHelp';
import LlmModelModal from './LlmModelModal';
import LlmProviderModal from './LlmProviderModal';

const LUCY_MIN_CONTEXT_WINDOW = 32000;

const keyLabel = (provider) => {
  if (provider.seeded) {
    return 'Releva token (env)';
  }
  return provider.hasApiKey ? `**** ${provider.apiKeyLast4}` : 'none';
};

const TestBadge = ({ model }) => {
  if (model.lastTestOk === true) {
    return <Badge color="green">ok</Badge>;
  }
  if (model.lastTestOk === false) {
    return <Badge color="red">failed {model.lastTestCategory}</Badge>;
  }
  return <Badge color="gray">not tested</Badge>;
};

// The probe returns only step names, timings, HTTP statuses and fixed categories, never upstream text.
const TestResult = ({ result }) => {
  if (result.error) {
    return <ErrorMessage message={result.error} />;
  }
  if (!result.steps?.length) {
    return (
      <Text size="xs" c="dimmed">
        The probe returned no steps.
      </Text>
    );
  }
  return (
    <Stack gap={2}>
      {result.steps.map((step) => (
        <Text key={step.name} size="xs" c={step.ok ? 'green' : 'red'}>
          {step.ok ? '✓' : '✗'} {step.name} · {step.ms} ms{step.status ? ` · HTTP ${step.status}` : ''}
          {step.category ? ` · ${step.category}` : ''}
        </Text>
      ))}
      {result.steps.some((step) => step.category === 'reasoning_required') && (
        <Text size="xs" c="dimmed">
          If this is a reasoning model, turn on Send reasoning back and Test again.
        </Text>
      )}
    </Stack>
  );
};

const SuperAdminAiModels = () => {
  const fetch = useFetch();
  const [loadError, setLoadError] = useState();
  const [providers, setProviders] = useState();
  const [llmModels, reloadLlmModels] = useLlmModels();
  const [editingProvider, setEditingProvider] = useState();
  const [editingModel, setEditingModel] = useState();
  const [expanded, setExpanded] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [lucySelection, setLucySelection] = useState();
  const [lucyError, setLucyError] = useState();

  const loadProviders = useCallback(() => {
    fetch('/api/admin/llmProviders')
      .then(({ llmProviders }) => setProviders(llmProviders))
      .catch(setLoadError);
  }, [fetch]);

  useEffect(loadProviders, [loadProviders]);

  const refresh = () => {
    loadProviders();
    reloadLlmModels();
  };

  const saved = (message) => {
    notifications.show({ message, color: 'success' });
    refresh();
  };

  const toggle = (url, body) =>
    fetch(url, { method: 'PUT', body: JSON.stringify(body) })
      .then(refresh)
      .catch((error) => notifications.show({ title: 'Not saved', message: error.message, color: 'red' }));

  const runTest = (model) => {
    setTesting((current) => ({ ...current, [model.id]: true }));
    fetch(`/api/admin/llmModels/${model.id}/test`, { method: 'POST' })
      .then(({ steps }) => setTestResults((current) => ({ ...current, [model.id]: { steps } })))
      .catch((error) => setTestResults((current) => ({ ...current, [model.id]: { error: error.message } })))
      .finally(() => {
        setTesting((current) => ({ ...current, [model.id]: false }));
        refresh();
      });
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (providers === undefined) {
    return <Spinner />;
  }

  const lucyModel = providers.flatMap((provider) => provider.llmModels ?? []).find((model) => model.role === 'lucy');
  const lucyModelValue = lucyModel ? String(lucyModel.id) : null;
  const lucyValue = lucySelection ?? lucyModelValue;
  const lucyOptions = (llmModels || [])
    .map(({ group, items }) => ({
      group,
      items: items.filter((item) => item.lastTestOk === true && item.contextWindow >= LUCY_MIN_CONTEXT_WINDOW),
    }))
    .filter(({ items }) => items.length > 0);

  const saveLucy = () => {
    setLucyError(undefined);
    fetch(`/api/admin/llmModels/${lucyValue}/role`, { method: 'PUT' })
      .then(() => {
        setLucySelection(undefined);
        saved('Lucy model saved');
      })
      .catch(setLucyError);
  };

  return (
    <Stack>
      <LlmProviderModal
        provider={editingProvider}
        onClose={() => setEditingProvider(undefined)}
        onSave={() => saved('Provider saved')}
      />
      <LlmModelModal
        model={editingModel?.model}
        provider={editingModel?.provider}
        onClose={() => setEditingModel(undefined)}
        onSave={() => saved('Model saved')}
      />

      <Card withBorder>
        <Title order={5} mb="sm">
          Default models
        </Title>
        <SettingRow label="Lucy model" htmlFor="lucyModel" help={<HelpPopover {...HELP.lucyModel} />}>
          <Group gap="xs" wrap="nowrap">
            <Select
              id="lucyModel"
              size="xs"
              w={320}
              data={withCurrent(lucyOptions, lucyModelValue, lucyModel?.label)}
              value={lucyValue}
              onChange={setLucySelection}
              allowDeselect={false}
              searchable
            />
            <Button size="xs" disabled={!lucyValue || lucyValue === lucyModelValue} onClick={saveLucy}>
              Save
            </Button>
          </Group>
        </SettingRow>
        {lucyError && <ErrorMessage message={lucyError.message} />}
      </Card>

      <Card withBorder>
        <Title order={5} mb="sm">
          Providers
        </Title>
        <Table.ScrollContainer minWidth={800}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th></Table.Th>
                <Table.Th>Label</Table.Th>
                <Table.Th>Key</Table.Th>
                <Table.Th>API style</Table.Th>
                <Table.Th>Base URL</Table.Th>
                <Table.Th>API key</Table.Th>
                <Table.Th>Enabled</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {providers.map((provider) => (
                <Fragment key={provider.id}>
                  <Table.Tr>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        aria-label={`Models of ${provider.label}`}
                        onClick={() => setExpanded({ ...expanded, [provider.id]: !expanded[provider.id] })}
                      >
                        {expanded[provider.id] ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                      </ActionIcon>
                    </Table.Td>
                    <Table.Td>
                      <strong>{provider.label}</strong>
                    </Table.Td>
                    <Table.Td>{provider.key}</Table.Td>
                    <Table.Td>
                      <Badge variant="light">{provider.apiStyle}</Badge>
                    </Table.Td>
                    <Table.Td>{provider.baseUrl || '(vendor default)'}</Table.Td>
                    <Table.Td>{keyLabel(provider)}</Table.Td>
                    <Table.Td>
                      <Switch
                        size="xs"
                        aria-label={`Enable ${provider.label}`}
                        checked={provider.enabled}
                        onChange={(e) =>
                          toggle(`/api/admin/llmProviders/${provider.id}`, {
                            llmProvider: { enabled: e.currentTarget.checked },
                          })
                        }
                      />
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label="Edit" position="top">
                        <ActionIcon variant="subtle" aria-label="Edit" onClick={() => setEditingProvider(provider)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                  {expanded[provider.id] && (
                    <Table.Tr>
                      <Table.Td colSpan={8}>
                        <Table>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Model id</Table.Th>
                              <Table.Th>Label</Table.Th>
                              <Table.Th>Context</Table.Th>
                              <Table.Th>Output</Table.Th>
                              <Table.Th>Test</Table.Th>
                              <Table.Th>Enabled</Table.Th>
                              <Table.Th></Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {(provider.llmModels ?? []).map((model) => (
                              <Fragment key={model.id}>
                                <Table.Tr>
                                  <Table.Td>{model.modelId}</Table.Td>
                                  <Table.Td>{model.label}</Table.Td>
                                  <Table.Td>{(model.contextWindow ?? 0).toLocaleString()}</Table.Td>
                                  <Table.Td>{(model.maxOutputTokens ?? 0).toLocaleString()}</Table.Td>
                                  <Table.Td>
                                    <Group gap="xs" wrap="nowrap">
                                      <TestBadge model={model} />
                                      {model.lastTestedAt && (
                                        <Text size="xs" c="dimmed">
                                          {new Date(model.lastTestedAt).toLocaleString()}
                                        </Text>
                                      )}
                                    </Group>
                                  </Table.Td>
                                  <Table.Td>
                                    <Switch
                                      size="xs"
                                      aria-label={`Enable ${model.label}`}
                                      checked={model.enabled}
                                      onChange={(e) =>
                                        toggle(`/api/admin/llmModels/${model.id}`, {
                                          llmModel: { enabled: e.currentTarget.checked },
                                        })
                                      }
                                    />
                                  </Table.Td>
                                  <Table.Td>
                                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                                      <Button
                                        size="compact-xs"
                                        variant="light"
                                        leftSection={<IconPlayerPlay size={12} />}
                                        loading={!!testing[model.id]}
                                        onClick={() => runTest(model)}
                                      >
                                        Test
                                      </Button>
                                      <Tooltip label="Edit" position="top">
                                        <ActionIcon
                                          variant="subtle"
                                          aria-label="Edit"
                                          onClick={() => setEditingModel({ model, provider })}
                                        >
                                          <IconPencil size={14} />
                                        </ActionIcon>
                                      </Tooltip>
                                    </Group>
                                  </Table.Td>
                                </Table.Tr>
                                {testResults[model.id] && (
                                  <Table.Tr>
                                    <Table.Td colSpan={7}>
                                      <TestResult result={testResults[model.id]} />
                                    </Table.Td>
                                  </Table.Tr>
                                )}
                              </Fragment>
                            ))}
                          </Table.Tbody>
                        </Table>
                        <Group justify="flex-end" mt="xs">
                          <Button size="xs" variant="light" onClick={() => setEditingModel({ model: {}, provider })}>
                            <IconPlus size={14} /> Add model
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Fragment>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Group justify="flex-end" mt="sm">
          <Button onClick={() => setEditingProvider({})}>
            <IconPlus size={14} /> Add provider
          </Button>
        </Group>
      </Card>
    </Stack>
  );
};

export default SuperAdminAiModels;
